import React, { useState, useEffect, useRef } from 'react';
import { TIMEZONE_LIST } from '../utils/timezones';
import { useAuth } from '../context/AuthContext';
import { useAvatar } from '../context/AvatarContext';
import { auth } from '../utils/api';
import PasswordRequirements from '../components/PasswordRequirements';
import CustomStatusManager from '../components/CustomStatusManager';
import NotificationPreferences from '../components/NotificationPreferences';
import CustomFieldsSection from '../components/Settings/CustomFieldsSection';
import TagManager from '../components/TagManager';

const Profile = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [formData, setFormData] = useState({
    companyName: '',
    firstName: '',
    lastName: '',
    email: '',
    pcgsApiKey: '',
    disableAutoSubUpdates: false,
    enableAutoPopulationUpdates: false,
    timezone: ''  // Don't set a default, let it be populated from API
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const fileInputRef = useRef(null);
  const { user, updateUser } = useAuth();
  const { avatarUrl, updateAvatar, deleteAvatar, handleImageError } = useAvatar();

  const tabs = [
    { id: 'profile', label: 'Profile Information' },
    { id: 'security', label: 'Security & Password' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'pcgs', label: 'PCGS Integration' },
    { id: 'customFields', label: 'Custom Fields' },
    { id: 'customization', label: 'Custom Statuses' },
    { id: 'tags', label: 'Tags' },
  ];

  // Fetch profile data
  const fetchProfile = async () => {
    try {
      const data = await auth.getCurrentUser();
      setFormData({
        companyName: data.company_name || '',
        firstName: data.first_name || '',
        lastName: data.last_name || '',
        email: data.email || '',
        pcgsApiKey: data.pcgs_api_key || '',
        disableAutoSubUpdates: data.disable_auto_sub_updates || false,
        enableAutoPopulationUpdates: data.enable_auto_population_updates || false,
        timezone: data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      });
      // Avatar URL is handled by AvatarContext through user data
    } catch (error) {
      console.error('Error fetching profile:', error);
      setMessage('Failed to load profile');
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Reset form data when canceling edit
      fetchProfile();
    }
    setIsEditing(!isEditing);
    setMessage('');
  };

  // Initial profile fetch and user context sync
  useEffect(() => {
    fetchProfile();
  }, []);

  // Keep formData in sync with user context
  useEffect(() => {
    if (user) {
      console.log('User context timezone:', user.timezone);
      console.log('Current formData timezone:', formData.timezone);
      setFormData(prev => ({
        ...prev,
        timezone: user.timezone || prev.timezone
      }));
    }
  }, [user]);

  // Debug timezone changes
  useEffect(() => {
    console.log('formData timezone updated:', formData.timezone);
  }, [formData.timezone]);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset file input to allow selecting the same file again
    e.target.value = '';

    setLoading(true);
    setMessage('');

    try {
      await updateAvatar(file);
      setMessage('Avatar updated successfully');
    } catch (error) {
      console.error('Avatar upload error:', error);
      setMessage(error.message || 'Error uploading avatar');
    } finally {
      setLoading(false);
    }
  };

  const handleTimezoneChange = async (newTimezone) => {
    setLoading(true);
    try {
      await auth.updateProfile({
        timezone: newTimezone
      });
      setFormData(prev => ({ ...prev, timezone: newTimezone }));
      // Update the user context with the new timezone
      await updateUser();
      setMessage('Timezone updated successfully');
    } catch (error) {
      console.error('Timezone update error:', error);
      setMessage(error.message || 'Error updating timezone');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      // Validate email format
      if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        setMessage('Please enter a valid email address');
        setLoading(false);
        return;
      }

      await auth.updateProfile({
        company_name: formData.companyName,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        pcgs_api_key: formData.pcgsApiKey,
        disable_auto_sub_updates: formData.disableAutoSubUpdates,
        enable_auto_population_updates: formData.enableAutoPopulationUpdates,
        timezone: formData.timezone
      });
      // Update the user context with the new profile data
      await updateUser();
      setMessage('Profile updated successfully');
      setIsEditing(false); // Exit edit mode after successful save
    } catch (error) {
      console.error('Profile update error:', error);
      setMessage(error.message || 'Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <div className="space-y-8">
            <h2 className="text-xl font-semibold text-center">Profile Information</h2>
            <div className="mb-8 text-center">
              <div className="flex flex-col items-center">
                <div className="relative inline-block">
                  <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-gray-200">
                    {avatarUrl ? (
                      <img 
                        src={avatarUrl} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                        onError={handleImageError}
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <span className="text-4xl text-gray-400">
                          {formData.firstName && formData.lastName 
                            ? `${formData.firstName[0]}${formData.lastName[0]}`.toUpperCase()
                            : formData.email?.[0]?.toUpperCase() || '?'}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 flex items-center justify-center space-x-2">
                    <button
                      onClick={handleAvatarClick}
                      className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      {avatarUrl ? 'Change' : 'Upload'}
                    </button>
                    {avatarUrl && (
                      <button
                        onClick={async () => {
                          setLoading(true);
                          try {
                            await deleteAvatar();
                            setMessage('Avatar removed successfully');
                          } catch (error) {
                            setMessage(error.message || 'Error removing avatar');
                          } finally {
                            setLoading(false);
                          }
                        }}
                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/jpeg,image/png,image/gif"
                onChange={handleAvatarChange}
              />
            </div>
            
            <div className="max-w-md mx-auto">
              <div className="flex justify-end mb-4">
                <button
                  type="button"
                  onClick={handleEditToggle}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {isEditing ? 'Cancel' : 'Edit Profile'}
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm ${
                      isEditing 
                        ? 'bg-white focus:ring-indigo-500 focus:border-indigo-500' 
                        : 'bg-gray-50 cursor-not-allowed'
                    }`}
                    readOnly={!isEditing}
                    placeholder="Enter your email"
                  />
                </div>

                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    value={formData.companyName}
                    onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                    className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm ${
                      isEditing 
                        ? 'bg-white focus:ring-indigo-500 focus:border-indigo-500' 
                        : 'bg-gray-50 cursor-not-allowed'
                    }`}
                    readOnly={!isEditing}
                    placeholder="Enter your company name"
                  />
                </div>

                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                    className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm ${
                      isEditing 
                        ? 'bg-white focus:ring-indigo-500 focus:border-indigo-500' 
                        : 'bg-gray-50 cursor-not-allowed'
                    }`}
                    readOnly={!isEditing}
                    placeholder="Enter your first name"
                  />
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm ${
                      isEditing 
                        ? 'bg-white focus:ring-indigo-500 focus:border-indigo-500' 
                        : 'bg-gray-50 cursor-not-allowed'
                    }`}
                    readOnly={!isEditing}
                    placeholder="Enter your last name"
                  />
                </div>

                <div>
                  <label htmlFor="timezone" className="block text-sm font-medium text-gray-700">
                    Timezone
                  </label>
                  <select
                    id="timezone"
                    value={formData.timezone}
                    onChange={(e) => handleTimezoneChange(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm bg-white focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {TIMEZONE_LIST.map(tz => (
                      <option key={tz} value={tz}>
                        {tz} ({new Date().toLocaleTimeString('en-US', { timeZone: tz, hour: '2-digit', minute: '2-digit' })})
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-sm text-gray-500">
                    All dates and times will be displayed in your selected timezone
                  </p>
                </div>

                {message && (
                  <div className={`p-3 rounded-md text-sm ${message.includes('success') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {message}
                  </div>
                )}

                {isEditing && (
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                )}
              </form>
            </div>
          </div>
        );
      case 'security':
        return (
          <div className="space-y-8">
            <h2 className="text-xl font-semibold text-center">Security Settings</h2>
            <div className="max-w-md mx-auto">
              <form onSubmit={async (e) => {
                e.preventDefault();
                setLoading(true);
                setMessage('');

                // Validate password requirements
                if (passwordData.newPassword.length < 8) {
                  setMessage('Password must be at least 8 characters long');
                  setLoading(false);
                  return;
                }
                if (!/[A-Z]/.test(passwordData.newPassword)) {
                  setMessage('Password must contain at least one uppercase letter');
                  setLoading(false);
                  return;
                }
                if (!/[a-z]/.test(passwordData.newPassword)) {
                  setMessage('Password must contain at least one lowercase letter');
                  setLoading(false);
                  return;
                }
                if (!/[0-9]/.test(passwordData.newPassword)) {
                  setMessage('Password must contain at least one number');
                  setLoading(false);
                  return;
                }
                if (!/[!@#$%^&*]/.test(passwordData.newPassword)) {
                  setMessage('Password must contain at least one special character (!@#$%^&*)');
                  setLoading(false);
                  return;
                }

                if (passwordData.newPassword !== passwordData.confirmPassword) {
                  setMessage('New passwords do not match');
                  setLoading(false);
                  return;
                }

                try {
                  await auth.changePassword(passwordData.currentPassword, passwordData.newPassword);
                  setMessage('Password updated successfully');
                  setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                } catch (error) {
                  console.error('Password change error:', error);
                  setMessage(error.message || 'Error changing password');
                } finally {
                  setLoading(false);
                }
              }} className="space-y-6">
                <div>
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                    Current Password
                  </label>
                  <input
                    type="password"
                    id="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    required
                  />
                </div>

                <PasswordRequirements 
                  password={passwordData.newPassword}
                  className="mb-4"
                />

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    required
                  />
                </div>

                {message && (
                  <div className={`p-3 rounded-md text-sm ${message.includes('success') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {loading ? 'Saving...' : 'Change Password'}
                </button>
              </form>
            </div>
          </div>
        );
      case 'notifications':
        return (
          <div className="space-y-8">
            <h2 className="text-xl font-semibold text-center">Notification Preferences</h2>
            <div className="max-w-2xl mx-auto">
              <NotificationPreferences />
            </div>
          </div>
        );
      case 'customization':
        return (
          <div className="space-y-8">
            <h2 className="text-xl font-semibold text-center">Custom Status Management</h2>
            <div className="max-w-2xl mx-auto">
              <CustomStatusManager />
            </div>
          </div>
        );
      case 'customFields':
        return (
          <div className="space-y-8">
            <h2 className="text-xl font-semibold text-center">Custom Fields</h2>
            <div className="max-w-2xl mx-auto">
              <CustomFieldsSection />
            </div>
          </div>
        );

      case 'tags':
        return (
          <div className="space-y-8">
            <h2 className="text-xl font-semibold text-center">Tag Management</h2>
            <div className="max-w-2xl mx-auto">
              <TagManager />
            </div>
          </div>
        );

      case 'pcgs':
        return (
          <div className="space-y-8">
            <h2 className="text-xl font-semibold text-center">PCGS Integration</h2>
            <div className="max-w-md mx-auto">
              <div className="bg-blue-50 rounded-md p-4 mb-6">
                <div className="space-y-2">
                  <p className="text-sm text-blue-700">
                    PCGS integration allows you to automatically look up coin information and track grading submissions.
                  </p>
                  <p className="text-sm text-blue-700">
                    An API key can be obtained by logging into PCGS and clicking "Generate Token" <a href="https://www.pcgs.com/publicapi/documentation" target="_blank" rel="noopener noreferrer" className="font-semibold text-indigo-700 hover:text-indigo-800">here</a>
                  </p>
                </div>
              </div>
              <div className="space-y-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label htmlFor="pcgsApiKey" className="block text-sm font-medium text-gray-700">
                        PCGS API Key
                      </label>
                      <button
                        type="button"
                        onClick={handleEditToggle}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        {isEditing ? 'Cancel' : 'Edit API Key'}
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type="password"
                        id="pcgsApiKey"
                        value={formData.pcgsApiKey}
                        onChange={(e) => setFormData(prev => ({ ...prev, pcgsApiKey: e.target.value }))}
                        className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm ${
                          isEditing 
                            ? 'bg-white focus:ring-indigo-500 focus:border-indigo-500' 
                            : 'bg-gray-50 cursor-not-allowed'
                        }`}
                        readOnly={!isEditing}
                        placeholder="Enter your PCGS API key"
                      />
                      <div className="mt-1 text-xs text-gray-500">
                        By entering a key, you agree to abide by the PCGS terms <a href="https://www.pcgs.com/publicapi" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800">here</a>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 border-t pt-6 space-y-6">
                    <div className="flex items-center justify-between">
                      <span className="flex-grow flex flex-col">
                        <span className="text-sm font-medium text-gray-700">Enable Auto Population Updates</span>
                        <span className="text-sm text-gray-500">
                          Automatically update population data for your PCGS coins once per week
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={async () => {
                          const newValue = !formData.enableAutoPopulationUpdates;
                          setFormData(prev => ({ 
                            ...prev, 
                            enableAutoPopulationUpdates: newValue
                          }));
                          setLoading(true);
                          try {
                            await auth.updateProfile({
                              enable_auto_population_updates: newValue
                            });
                            await updateUser();
                            setMessage('Auto population updates setting saved successfully');
                          } catch (error) {
                            console.error('Update error:', error);
                            setMessage(error.message || 'Error updating setting');
                            // Revert the toggle if save failed
                            setFormData(prev => ({
                              ...prev,
                              enableAutoPopulationUpdates: !newValue
                            }));
                          } finally {
                            setLoading(false);
                          }
                        }}
                        className={`${
                          formData.enableAutoPopulationUpdates ? 'bg-indigo-600' : 'bg-gray-200'
                        } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                      >
                        <span
                          className={`${
                            formData.enableAutoPopulationUpdates ? 'translate-x-5' : 'translate-x-0'
                          } pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="flex-grow flex flex-col">
                        <span className="text-sm font-medium text-gray-700">Enable Auto Submission Updates</span>
                        <span className="text-sm text-gray-500">
                          Turn on automatic checking for PCGS submission status
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={async () => {
                          const newValue = !formData.disableAutoSubUpdates;
                          setFormData(prev => ({ 
                            ...prev, 
                            disableAutoSubUpdates: newValue
                          }));
                          setLoading(true);
                          try {
                            await auth.updateProfile({
                              disable_auto_sub_updates: newValue
                            });
                            await updateUser();
                            setMessage('Auto submission updates setting saved successfully');
                          } catch (error) {
                            console.error('Update error:', error);
                            setMessage(error.message || 'Error updating setting');
                            // Revert the toggle if save failed
                            setFormData(prev => ({
                              ...prev,
                              disableAutoSubUpdates: !newValue
                            }));
                          } finally {
                            setLoading(false);
                          }
                        }}
                        className={`${
                          !formData.disableAutoSubUpdates ? 'bg-indigo-600' : 'bg-gray-200'
                        } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                      >
                        <span
                          className={`${
                            !formData.disableAutoSubUpdates ? 'translate-x-5' : 'translate-x-0'
                          } pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
                        />
                      </button>
                    </div>
                  </div>

                  {message && (
                    <div className={`p-3 rounded-md text-sm ${message.includes('success') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {message}
                    </div>
                  )}

                  {isEditing && (
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  )}
                </form>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-x-5">
          {/* Sidebar */}
          <aside className="py-6 px-2 sm:px-6 lg:col-span-3">
            <nav className="space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setMessage(''); // Clear any messages when switching tabs
                  }}
                  className={`${
                    activeTab === tab.id
                      ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                      : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } group w-full flex items-center px-3 py-2 text-sm font-medium border-l-4`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Main content */}
          <div className="space-y-6 sm:px-6 lg:px-0 lg:col-span-9">
            <div className="shadow sm:rounded-md sm:overflow-hidden">
              <div className="bg-white py-8 px-6">
                {renderTabContent()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
